import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { sharedApi, SHARED_ORIGIN } from "@/lib/api";
import { MapPin, Loader2, Search, X } from "lucide-react";

const CLASSIFIEDS_BASE = SHARED_ORIGIN;

/**
 * Address autocomplete + static-map preview.
 * Uses the FIXED shared-backend proxies:
 *   GET /api/geo/places/autocomplete?q=&country=nz  (auth)
 *   GET /api/geo/places/details?place_id=            (auth)
 *   GET /api/geo/places/static-map?lat=&lng=          (public)
 */
export default function AddressPicker({ value, onChange }) {
  const [query, setQuery] = useState(value?.address || "");
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value?.address || "");
  }, [value?.address]);

  const detailsMut = useMutation({
    mutationFn: async (place_id) => {
      const { data } = await sharedApi.get("/geo/places/details", { params: { place_id } });
      return data;
    },
  });

  const fetchPredictions = (q) => {
    if (!q || q.length < 3) { setPredictions([]); return; }
    setLoading(true);
    sharedApi
      .get("/geo/places/autocomplete", { params: { q, country: "nz" } })
      .then(({ data }) => setPredictions(data.predictions || data.results || []))
      .catch(() => setPredictions([]))
      .finally(() => setLoading(false));
  };

  const onInput = (v) => {
    setQuery(v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(v), 300);
  };

  const pick = async (p) => {
    setOpen(false);
    setPredictions([]);
    const description = p.description || p.formatted_address || p.address || "";
    setQuery(description);
    try {
      const details = await detailsMut.mutateAsync(p.place_id);
      onChange({
        address: description,
        lat: details.lat ?? details.geometry?.location?.lat ?? null,
        lng: details.lng ?? details.geometry?.location?.lng ?? null,
        pincode: details.postal_code || details.pincode || "",
        area: details.suburb || details.area || "",
        city: details.city || details.locality || "",
        region: details.region || details.administrative_area || "",
      });
    } catch {
      onChange({ address: description });
    }
  };

  const staticMapSrc = useMemo(
    () => (value?.lat && value?.lng)
      ? `${CLASSIFIEDS_BASE}/api/geo/places/static-map?lat=${value.lat}&lng=${value.lng}`
      : null,
    [value?.lat, value?.lng],
  );

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          data-testid="post-ad-address-search"
          value={query}
          onChange={(e) => onInput(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Start typing an address in New Zealand…"
          className="w-full pl-11 pr-10 py-3 rounded-xl bg-white border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
        />
        {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
        {query && !loading && (
          <button
            type="button"
            onClick={() => { setQuery(""); setPredictions([]); onChange({ address: "", lat: null, lng: null, pincode: "", area: "", city: "", region: "" }); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500"
            aria-label="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {open && predictions.length > 0 && (
          <ul className="absolute z-30 mt-1 left-0 right-0 rounded-xl bg-white shadow-dropdown border border-soft py-1 max-h-72 overflow-y-auto">
            {predictions.map((p) => (
              <li key={p.place_id}>
                <button
                  type="button"
                  onClick={() => pick(p)}
                  data-testid={`post-ad-address-suggestion-${p.place_id}`}
                  className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-[color:var(--brand-blue-wash)]"
                >
                  <MapPin className="w-3.5 h-3.5 inline mr-1 text-[color:var(--brand-blue)]" />
                  {p.description || p.formatted_address}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {staticMapSrc && (
        <div className="rounded-xl overflow-hidden bg-slate-100">
          <img src={staticMapSrc} alt="Selected location" className="w-full h-auto" data-testid="post-ad-address-map" />
        </div>
      )}
    </div>
  );
}
